import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  X,
  Send,
  Bot,
  Sparkles,
  User,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Users,
  HelpCircle,
  Maximize2,
  Minimize2,
  PlusCircle,
  History,
  Trash2,
  Clock,
  ChevronLeft,
} from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const getInitialMessages = (user) => {
  const firstName = user?.name ? user.name.trim().split(" ")[0] : null;
  const greetingText = firstName
    ? `👋 Hi ${firstName}! How can I help with your group expenses today?`
    : "👋 Hi there! How can I help with your group expenses today?";

  const chips = firstName
    ? ["How to settle up?", "Add new expense", "Export reports"]
    : ["How GEMS works", "Is it free?", "Request demo"];

  return [
    {
      id: "init-1",
      sender: "bot",
      text: greetingText,
      chips: chips,
    },
  ];
};

export default function AIChatbot() {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [messages, setMessages] = useState(() => getInitialMessages(user));
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [leadStep, setLeadStep] = useState(null); // 'name' | 'email' | 'groupType' | 'submitted'
  const [leadData, setLeadData] = useState({ name: "", email: "", phone: "", group_type: "Trip", group_size: "5-10", message: "" });
  
  // History & Session Management (Only for logged-in users)
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(() => "session_" + Date.now());

  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  // Load saved sessions from localStorage ONLY when user is logged in
  useEffect(() => {
    if (!user || !user.id) {
      setSessions([]);
      setShowHistory(false);
      return;
    }
    try {
      const savedSessions = localStorage.getItem(`gems_chatbot_sessions_${user.id}`);
      if (savedSessions) {
        setSessions(JSON.parse(savedSessions));
      } else {
        setSessions([]);
      }
    } catch (e) {
      console.error("Failed to parse saved chat sessions", e);
    }
  }, [user]);

  // Update initial message greeting dynamically when user logs in or out if user hasn't chatted yet
  useEffect(() => {
    if (messages.length === 1 && messages[0].id === "init-1") {
      setMessages(getInitialMessages(user));
    }
  }, [user]);

  // Save sessions to localStorage ONLY for logged in users
  const saveSessionsToStorage = (updatedSessions) => {
    setSessions(updatedSessions);
    if (!user || !user.id) return;
    try {
      localStorage.setItem(`gems_chatbot_sessions_${user.id}`, JSON.stringify(updatedSessions));
    } catch (e) {
      console.error("Failed to save chat sessions", e);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showHistory) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping, showHistory]);

  const handleOpen = () => {
    setIsOpen(true);
    setHasUnread(false);
  };

  const addMessage = (sender, text, chips = null, customContent = null) => {
    const newMessage = { id: String(Date.now() + Math.random()), sender, text, chips, customContent };
    setMessages((prev) => {
      const nextMessages = [...prev, newMessage];
      if (user && user.id) {
        autoSaveCurrentSession(nextMessages);
      }
      return nextMessages;
    });
  };

  // Save or update active session in history list (LOGGED-IN USERS ONLY)
  const autoSaveCurrentSession = (activeMessages) => {
    if (!user || !user.id) return; // Do not save history for anonymous users
    if (activeMessages.length <= 1) return; // Don't save initial single message greeting

    const userMsgs = activeMessages.filter((m) => m.sender === "user");
    if (userMsgs.length === 0) return;

    const sessionTitle = userMsgs[0].text.length > 35 ? userMsgs[0].text.substring(0, 35) + "..." : userMsgs[0].text;
    const updatedAt = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    setSessions((prev) => {
      const existingIdx = prev.findIndex((s) => s.id === currentSessionId);
      const sessionObj = {
        id: currentSessionId,
        title: sessionTitle,
        messages: activeMessages,
        timestamp: updatedAt,
      };

      let updated;
      if (existingIdx >= 0) {
        updated = [...prev];
        updated[existingIdx] = sessionObj;
      } else {
        updated = [sessionObj, ...prev];
      }

      try {
        localStorage.setItem(`gems_chatbot_sessions_${user.id}`, JSON.stringify(updated));
      } catch (e) {
        console.error("Storage save failed", e);
      }
      return updated;
    });
  };

  const handleNewChat = () => {
    if (user && user.id && messages.length > 1) {
      autoSaveCurrentSession(messages);
    }
    const newId = "session_" + Date.now();
    setCurrentSessionId(newId);
    setMessages(getInitialMessages(user));
    setLeadStep(null);
    setShowHistory(false);
    toast.success("Started a new chat session");
  };

  const handleSelectSession = (session) => {
    setCurrentSessionId(session.id);
    setMessages(session.messages || getInitialMessages(user));
    setShowHistory(false);
  };

  const handleDeleteSession = (sessionId, e) => {
    e.stopPropagation();
    const updated = sessions.filter((s) => s.id !== sessionId);
    saveSessionsToStorage(updated);
    if (currentSessionId === sessionId) {
      handleNewChat();
    } else {
      toast.success("Chat history session deleted");
    }
  };

  const handleChipClick = (chipText) => {
    processUserMessage(chipText);
  };

  const processUserMessage = async (text) => {
    if (!text.trim()) return;
    addMessage("user", text);
    setInputValue("");
    setIsTyping(true);

    const lower = text.toLowerCase();

    // 1. Handle active lead capture flow
    if (leadStep === "name") {
      setLeadData((prev) => ({ ...prev, name: text }));
      setLeadStep("contact");
      setIsTyping(false);
      addMessage("bot", `Nice to meet you, ${text}! What's your email address or phone number so we can reach out?`);
      return;
    }

    if (leadStep === "contact") {
      setLeadData((prev) => ({ ...prev, email: text }));
      setLeadStep("type");
      setIsTyping(false);
      addMessage("bot", "Great! What kind of group are you planning for?", [
        "Vacation / Trip",
        "Flatmates / House",
        "Events & Parties",
        "Project Crew",
      ]);
      return;
    }

    if (leadStep === "type") {
      const updatedLead = { ...leadData, group_type: text, message: "Requested setup via AI Chatbot" };
      setLeadData(updatedLead);
      setLeadStep("submitted");

      try {
        await api.post("/leads", updatedLead);
        toast.success("Lead captured! Our team will reach out soon.");
      } catch (e) {
        console.error("Lead submission error", e);
      }

      setIsTyping(false);
      addMessage(
        "bot",
        `🎉 Thanks ${updatedLead.name}! We've saved your request for a ${text} group setup. Our team will send you full details shortly!`,
        null,
        { type: "leadSuccess" }
      );
      return;
    }

    // 2. Check direct trigger for lead capture setup
    if (
      lower === "request demo" ||
      lower === "get custom setup / demo" ||
      lower.includes("request demo") ||
      lower.includes("get demo")
    ) {
      setLeadStep("name");
      setIsTyping(false);
      addMessage("bot", "I'd be happy to arrange a personalized setup and demo for your team! What is your name?");
      return;
    }

    // 3. Call Gemini Backend API Endpoint `/api/chat`
    try {
      const historyPayload = messages
        .filter((m) => m.text && !m.chips)
        .map((m) => ({ sender: m.sender, text: m.text }));

      const res = await api.post("/chat", {
        message: text,
        history: historyPayload,
        page_context: {
          path: location.pathname,
          title: document.title || "GEMS",
        },
        user_context: {
          is_logged_in: Boolean(user),
          user_name: user?.name || null,
          user_email: user?.email || null,
          user_role: user?.role || null,
          plan: user?.plan || "Free Beta",
        },
      });

      setIsTyping(false);
      if (res.data?.reply) {
        let responseChips = null;
        if (
          res.data.reply.toLowerCase().includes("demo") ||
          res.data.reply.toLowerCase().includes("setup")
        ) {
          responseChips = ["Request demo", "How GEMS works"];
        }
        addMessage("bot", res.data.reply, responseChips);
      } else {
        throw new Error("No response content");
      }
    } catch (err) {
      console.warn("Gemini API call failed, using smart fallback", err);
      setIsTyping(false);

      if (lower.includes("work") || lower.includes("how it works")) {
        addMessage(
          "bot",
          "GEMS works in 3 easy steps:\n1️⃣ Create a group link.\n2️⃣ Invite members.\n3️⃣ Log expenses & settle up with a double-entry ledger!",
          ["Request demo", "Is it free?"]
        );
      } else if (lower.includes("free")) {
        addMessage(
          "bot",
          "Yes! GEMS is 100% free for small crews in public beta with no credit card required.",
          ["Create Account Now", "Request demo"]
        );
      } else if (lower.includes("settle")) {
        addMessage(
          "bot",
          "You can settle up directly inside any group from the **Groups** menu using our smart settlement calculator!",
          ["Add new expense", "Export reports"]
        );
      } else {
        addMessage(
          "bot",
          "I'm here to help with your group expenses! Would you like a quick demo or customized setup?",
          ["Request demo", "How GEMS works", "Is it free?"]
        );
      }
    }
  };

  return (
    <>
      {/* Floating Toggle Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {!isOpen && (
          <button
            onClick={handleOpen}
            data-testid="ai-chatbot-launcher"
            title="Open AI Assistant"
            className="group relative w-14 h-14 rounded-full bg-slate-900 text-white shadow-2xl hover:bg-slate-800 active:scale-95 transition-all duration-200 border border-emerald-500/40 grid place-items-center"
          >
            <div className="relative grid place-items-center">
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 grid place-items-center">
                <Sparkles className="w-5 h-5 text-emerald-400 animate-pulse" />
              </div>
              {hasUnread && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 ring-2 ring-slate-900 animate-ping" />
              )}
            </div>
          </button>
        )}
      </div>

      {/* Chat Dialog */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className={`fixed z-50 bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col transition-all duration-300 ${
              isFullscreen
                ? "inset-2 sm:inset-6 w-auto h-auto"
                : "bottom-6 right-4 sm:right-6 w-[92vw] sm:w-[420px] h-[560px]"
            }`}
            data-testid="ai-chatbot-modal"
          >
            {/* Header */}
            <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                {showHistory ? (
                  <button
                    onClick={() => setShowHistory(false)}
                    className="w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center text-slate-300 hover:text-white transition-colors"
                    title="Back to Chat"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                ) : (
                  <div className="relative">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 grid place-items-center">
                      <Bot className="w-5 h-5 text-emerald-400" />
                    </div>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-900" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="heading text-sm font-bold text-white">
                      {showHistory ? "Chat History" : "GEMS AI Assistant"}
                    </h3>
                    {!showHistory && (
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30">
                        Online
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Control Buttons */}
              <div className="flex items-center gap-1">
                {/* New Chat Button */}
                <button
                  onClick={handleNewChat}
                  className="w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center text-slate-400 hover:text-emerald-400 transition-colors"
                  title="Start New Chat"
                  data-testid="ai-chatbot-new-chat"
                >
                  <PlusCircle className="w-4.5 h-4.5" />
                </button>

                {/* History Button - ONLY rendered if user is LOGGED IN */}
                {Boolean(user) && (
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className={`w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center transition-colors ${
                      showHistory ? "text-emerald-400 bg-white/10" : "text-slate-400 hover:text-white"
                    }`}
                    title="View History"
                    data-testid="ai-chatbot-history-toggle"
                  >
                    <History className="w-4.5 h-4.5" />
                  </button>
                )}

                {/* Fullscreen Toggle */}
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center text-slate-400 hover:text-white transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                  data-testid="ai-chatbot-fullscreen-toggle"
                >
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>

                {/* Close Button */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-white/10 grid place-items-center text-slate-400 hover:text-white transition-colors"
                  data-testid="ai-chatbot-close"
                  title="Close Assistant"
                >
                  <X className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>

            {/* History Panel View (Only accessible for logged in users) */}
            {showHistory && Boolean(user) ? (
              <div className="flex-1 p-4 overflow-y-auto bg-slate-50/50 space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200/80 text-xs font-semibold text-slate-500">
                  <span>Saved Sessions ({sessions.length})</span>
                  {sessions.length > 0 && (
                    <button
                      onClick={() => {
                        if (window.confirm("Clear all chat history?")) {
                          saveSessionsToStorage([]);
                          handleNewChat();
                        }
                      }}
                      className="text-[11px] text-rose-600 hover:underline flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Clear All
                    </button>
                  )}
                </div>

                {sessions.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 space-y-2">
                    <Clock className="w-8 h-8 mx-auto stroke-1 text-slate-300" />
                    <p className="text-xs">No saved chat sessions yet.</p>
                  </div>
                ) : (
                  sessions.map((sess) => (
                    <div
                      key={sess.id}
                      onClick={() => handleSelectSession(sess)}
                      className={`p-3 rounded-2xl border transition-all cursor-pointer flex items-center justify-between group ${
                        sess.id === currentSessionId
                          ? "bg-emerald-50/80 border-emerald-300 shadow-sm"
                          : "bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-xs"
                      }`}
                    >
                      <div className="space-y-1 flex-1 pr-2 min-w-0">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <h4 className="text-xs font-semibold text-slate-800 truncate">{sess.title}</h4>
                        </div>
                        <p className="text-[10px] text-slate-400">
                          {sess.messages?.length || 0} messages • {sess.timestamp || "Just now"}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleDeleteSession(sess.id, e)}
                        className="w-7 h-7 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-rose-50 text-slate-400 hover:text-rose-600 grid place-items-center transition-all shrink-0"
                        title="Delete Session"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            ) : (
              /* Message Area */
              <div className="flex-1 p-4 overflow-y-auto space-y-3.5 bg-slate-50/50">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex flex-col ${m.sender === "user" ? "items-end" : "items-start"}`}
                  >
                    <div
                      className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-xs leading-relaxed ${
                        m.sender === "user"
                          ? "bg-slate-900 text-white rounded-br-none"
                          : "bg-white text-slate-800 border border-slate-200/80 shadow-sm rounded-bl-none"
                      }`}
                    >
                      <p className="whitespace-pre-line">{m.text}</p>

                      {/* Custom Render Content */}
                      {m.customContent?.type === "leadSuccess" && (
                        <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-col gap-2">
                          <button
                            onClick={() => {
                              setIsOpen(false);
                              navigate("/register");
                            }}
                            className="w-full py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-95 transition-all"
                          >
                            Create Group Now <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      {m.customContent?.type === "ctaRegister" && (
                        <div className="mt-2.5">
                          <button
                            onClick={() => {
                              setIsOpen(false);
                              navigate("/register");
                            }}
                            className="w-full py-2 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center justify-center gap-1.5"
                          >
                            Get Started Free <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Suggestion Chips */}
                    {m.chips && m.chips.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2 max-w-[90%]">
                        {m.chips.map((chip, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleChipClick(chip)}
                            className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 transition-colors shadow-xs"
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {isTyping && (
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200/80 rounded-2xl w-16 text-slate-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0.2s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0.4s]" />
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>
            )}

            {/* Input Bar */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                processUserMessage(inputValue);
              }}
              className="p-3 bg-white border-t border-slate-200 flex items-center gap-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={
                  leadStep === "name"
                    ? "Enter your name..."
                    : leadStep === "contact"
                    ? "Enter email or phone..."
                    : "Ask AI or request a demo..."
                }
                className="flex-1 h-10 px-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="ai-chatbot-input"
              />
              <button
                type="submit"
                disabled={!inputValue.trim()}
                className="w-10 h-10 rounded-xl bg-slate-900 disabled:opacity-40 hover:bg-slate-800 text-white grid place-items-center transition-all"
                data-testid="ai-chatbot-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
